<template>
    <div>
        <v-menu
            v-model="menuOpen"
            :close-on-content-click="false"
            offset-x
            rounded
            :attach="false"
            :content-class="'filters-menu-content'"
            :eager="true"
            :nudge-width="0"
            :open-on-click="true"
            :transition="'fade-transition'"
            :max-width="600"
            role="dialog"
            :aria-modal="true"
            :aria-label="'Filter menu'"
            :aria-expanded="String(menuOpen)"
            :aria-controls="menuContentId"
            @keydown.esc="menuOpen = false"
            @keydown.esc.prevent.stop="closeMenuAndFocusActivator">
            <template v-slot:activator="{ on, attrs }">
                <!-- SINGLE, persistent activator node -->
                <span
                    ref="filterActivator"
                    class="filter-activator"
                    role="button"
                    tabindex="0"
                    v-on="on"
                    v-bind="attrs"
                    :aria-expanded="String(menuOpen)"
                    :aria-controls="menuContentId"
                    aria-haspopup="dialog"
                    @keydown.enter.prevent="menuOpen = true"
                    @keydown.space.prevent="menuOpen = true">
                    <!-- Only visual content switches; activator node stays -->
                    <v-slide-x-transition hide-on-leave mode="out-in">
                        <span v-if="filters.length === 0">
                            <v-btn tabindex="-1" :ripple="false" :small="dense" rounded class="mr-1" color="primary" aria-hidden="true">
                                <v-icon class="mr-1">mdi-filter-variant</v-icon>
                                Filter
                            </v-btn>
                        </span>
                        <span v-else>
                            <v-chip tabindex="-1" class="pointer" small close aria-hidden="true" @click:close="filters = []">
                                <v-icon close-icon="mdi-close" small color="primary" class="mr-1">mdi-filter-variant</v-icon>
                                {{ filters.length }} {{ filters.length === 1 ? " active filter" : " active filters" }}
                            </v-chip>
                        </span>
                    </v-slide-x-transition>
                </span>

                <!-- Live region for SR users -->
                <span class="sr-only" aria-live="polite">
                    {{ filters.length === 0 ? "No active filters" : filters.length + (filters.length === 1 ? " active filter" : " active filters") }}
                </span>
            </template>

            <v-card v-focus-trap="menuOpen" rounded>
                <v-card-text>
                    <v-row>
                        <v-col cols="4">
                            <v-select ref="fieldSelect" hide-details rounded filled v-model="selectedField" :items="fields" label="Field to filter" item-value="key" item-text="name" />
                        </v-col>
                        <v-col cols="4">
                            <!-- Subfield-Dropdown nur für Felder mit subfields -->
                            <v-select
                                data-focus-first
                                v-if="selectedFieldObj && selectedFieldObj.subfields"
                                hide-details
                                rounded
                                filled
                                v-model="selectedSubField"
                                :items="selectedFieldObj.subfields || []"
                                label="Type"
                                item-text="label"
                                item-value="value"></v-select>
                            <!-- Operator-Feld für "position" Subfield -->
                            <v-select
                                v-if="selectedFieldObj && selectedFieldObj.subfields && selectedSubField === 'position'"
                                class="mt-2"
                                hide-details
                                rounded
                                filled
                                v-model="selectedOperator"
                                :items="operators"
                                label="Operator"></v-select>
                            <!-- Operator wie gehabt für alle anderen Felder -->
                            <v-select v-else-if="selectedFieldObj && !selectedFieldObj.subfields" hide-details rounded filled v-model="selectedOperator" :items="operators" label="Operator"></v-select>
                        </v-col>
                        <v-col cols="4">
                            <div>
                                <!-- Value-Auswahl für Subfield -->
                                <v-text-field
                                    v-if="selectedFieldObj && selectedFieldObj.subfields && selectedSubField === 'position'"
                                    hide-details
                                    rounded
                                    filled
                                    v-model="filterValue"
                                    label="Value"
                                    @keyup.enter="addFilter" />
                                <v-select
                                    v-else-if="selectedFieldObj && selectedFieldObj.subfields && selectedSubField === 'ai_reference'"
                                    hide-details
                                    rounded
                                    filled
                                    v-model="filterValue"
                                    :items="[true, false]"
                                    label="Value" />
                                <!-- Standard Value-Auswahl für andere Felder -->
                                <v-autocomplete v-else-if="optionsData.values" hide-details rounded filled v-model="filterValue" :items="optionsData.values" label="Value"></v-autocomplete>
                                <v-text-field v-else hide-details rounded filled v-model="filterValue" label="Value" @keyup.enter="addFilter" />
                            </div>
                        </v-col>
                    </v-row>
                    <v-btn ref="addFilterBtn" rounded @click="addFilter" color="primary">Add Filter</v-btn>
                </v-card-text>
                <v-divider v-if="filters.length"></v-divider>
                <v-card-text v-if="filters.length">
                    <span v-for="(filter, index) in filters" :key="index">
                        <v-chip
                            class="mr-2"
                            color=""
                            close
                            :tabindex="0"
                            role="button"
                            @click:close="removeFilter(index)"
                            @keydown.enter.prevent="removeFilter(index)"
                            @keydown.space.prevent="removeFilter(index)"
                            @keydown.delete.prevent="removeFilter(index)"
                            :aria-label="'Remove filter ' + (filter.field && filter.field.name ? filter.field.name : index + 1)">
                            <v-icon color="primary" small class="mr-1">mdi-filter-variant</v-icon>
                            {{ filter.field.name }}
                            <span class="mx-1"
                                ><b>{{ filter.operator.name }}</b></span
                            >
                            {{ filter.value }}
                        </v-chip>
                    </span>
                </v-card-text>
            </v-card>
        </v-menu>
    </div>
</template>

<script>
    export default {
        props: {
            fields: Array,
            dense: Boolean,
            activeField: String,
            initialFilters: {
                type: Array,
                default: null
            }
        },
        data() {
            return {
                selectedField: "",
                selectedSubField: "",
                selectedOperator: "",
                filterValue: "",
                filters: [],
                menuOpen: false,
                filterLinkOperator: "AND",
                filterLinkOperators: ["AND", "OR"],
                menuContentId: "filters-menu-" + Math.random().toString(36).slice(2, 8),
                _suppressFiltersEmit: false
            }
        },
        watch: {
            filters() {
                if (this._suppressFiltersEmit) return
                this.$emit("filtersUpdated", this.filters)
            },
            activeField: {
                immediate: true,
                handler(newVal) {
                    if (newVal) {
                        this.selectedField = newVal
                        this.menuOpen = true
                    }
                }
            },
            initialFilters: {
                immediate: true,
                handler(newVal) {
                    if (!Array.isArray(newVal)) return
                    this._suppressFiltersEmit = true
                    this.filters = newVal
                    this.$nextTick(() => {
                        this._suppressFiltersEmit = false
                    })
                }
            }
        },
        computed: {
            optionsData() {
                if (!this.selectedField) return { values: null }
                let field = this.fields.find((f) => f.key === this.selectedField)
                try {
                    return { values: field && field.options ? field.options.values : null }
                } catch (error) {
                    return { values: null }
                }
            },
            selectedFieldObj() {
                return this.fields.find((f) => f.key === this.selectedField) || {}
            },
            operators() {
                if (!this.selectedFieldObj || (this.selectedFieldObj.subfields && !this.selectedSubField)) return []
                if (this.selectedFieldObj.subfields && this.selectedSubField) {
                    if (this.selectedSubField === "ai_reference") {
                        return ["equals"]
                    }
                    if (this.selectedSubField === "position") {
                        return ["equals", "greater than", "less than"]
                    }
                }
                // Standard-Operatoren wie gehabt
                let numbers = ["equals", "greater than", "less than"]
                let text = ["equals", "contains", "does not contain"]
                let options = ["equals", "not equals"]
                let arrayOptions = ["includes", "excludes"]
                let field = this.fields.find((f) => f.key === this.selectedField)
                if (field && field.type == "number") {
                    return numbers
                }
                if (field && field.type == "text") {
                    return text
                }
                if (field && field.type == "array") {
                    return arrayOptions
                }
                return options
            }
        },
        methods: {
            closeMenuAndFocusActivator() {
                this.menuOpen = false
                this.$nextTick(() => {
                    const a = this.$refs.filterActivator
                    const el = a && (a.$el || a)
                    el && el.focus && el.focus()
                })
            },
            handleTab(e) {
                // Move focus to the first focusable element inside the card
                const root = this.$el.querySelector("#" + this.menuContentId)
                if (!root) return
                const focusables = root.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])')
                if (focusables.length) focusables[0].focus()
            },
            handleShiftTab(e) {
                // Move focus to the last focusable element inside the card
                const root = this.$el.querySelector("#" + this.menuContentId)
                if (!root) return
                const focusables = root.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])')
                if (focusables.length) focusables[focusables.length - 1].focus()
            },
            getDecryptedName(item) {
                if (item && item.length > 0) {
                    let name = this.CryptoJS.AES.decrypt(item, process.env.VUE_APP_ENCRYPTION_PW).toString(this.CryptoJS.enc.Utf8)
                    return name
                } else {
                    return null
                }
            },
            openFilterMenu() {
                this.menuOpen = true
            },
            addFilter() {
                // Für ai_reference: Operator automatisch auf "equals" setzen
                let operator = this.selectedOperator
                if (this.selectedFieldObj && this.selectedFieldObj.subfields && this.selectedSubField === "ai_reference") {
                    operator = "equals"
                }

                if (this.selectedField && operator && this.filterValue !== "" && (!this.selectedFieldObj.subfields || this.selectedSubField)) {
                    const fieldObj = this.fields.find((field) => field.key === this.selectedField)
                    this.filters.push({
                        field: fieldObj,
                        operator: { name: operator, value: this.getOperatorValue(operator) },
                        value: this.filterValue,
                        subfield: fieldObj && fieldObj.subfields ? this.selectedSubField : undefined
                    })

                    // Eingaben leeren
                    this.selectedField = ""
                    this.selectedOperator = ""
                    this.filterValue = ""
                    this.selectedSubField = ""

                    // Nach DOM-Update: Fokus zurück auf "Add Filter" (WCAG 2.4.3/2.1.1)
                    this.$nextTick(() => {
                        const btn = this.$refs.addFilterBtn
                        if (btn) {
                            // v-btn ist ein Vue-Component -> Root-Element fokussieren
                            ;(btn.$el || btn).focus && (btn.$el || btn).focus()
                        }
                    })
                }
            },
            removeFilter(index) {
                this.filters.splice(index, 1)

                // Nach DOM-Update: Fokus zurück auf das erste Feld im Menü
                this.$nextTick(() => {
                    const sel = this.$refs.fieldSelect
                    if (!sel) return

                    // Vuetify v2: v-select hat i.d.R. focus()
                    if (typeof sel.focus === "function") {
                        sel.focus()
                        return
                    }

                    // Fallback: innere Eingabe fokussieren
                    const el = sel.$el || sel
                    const input = el && el.querySelector('input, [role="combobox"], .v-input__slot input')
                    if (input && typeof input.focus === "function") input.focus()
                })
            },
            getOperatorValue(operator) {
                switch (operator) {
                    case "equals":
                        return "equals"
                    case "includes":
                        return "includes"
                    case "excludes":
                        return "excludes"
                    case "greater than":
                        return "greaterThan"
                    case "less than":
                        return "lessThan"
                    case "contains":
                        return "contains"
                    case "does not contain":
                        return "doesNotContain"
                    default:
                        break
                }
            }
        }
    }
</script>
<style scoped>
    .pointer:hover {
        cursor: pointer !important;
    }
    .pointer {
        margin-top: 6px;
    }
    .sr-only {
        position: absolute !important;
        width: 1px !important;
        height: 1px !important;
        padding: 0 !important;
        margin: -1px !important;
        overflow: hidden !important;
        clip: rect(0, 0, 0, 0) !important;
        white-space: nowrap !important;
        border: 0 !important;
    }
</style>
